import {
  type Block,
  type ColorItem,
  type ID,
  type LibrarySymbol,
  resolveSymbol,
  type SymbolItem,
} from '@octopus-do/octopus-project-core'
import type { AppState } from '@store/types'
import { useSelector } from 'react-redux'

import type { HydratedBlock } from './types'

type HydrationBlocksData = {
  blocks: Record<ID, Block>
  symbols: SymbolItem[]
  /** Library refs resolve from here, whatever the symbols source. */
  library: Record<ID, LibrarySymbol>
  colors: ColorItem[]
} | null

// @todo review optional passing data
export const useGetHydratedBlocks = (data: HydrationBlocksData = null) => {
  const blocks = data
    ? data.blocks
    : useSelector((state: AppState) => state.manager.project.blocks)
  const symbols = data
    ? data.symbols
    : useSelector((state: AppState) => state.manager.project.symbols)
  const library = data
    ? data.library
    : useSelector((state: AppState) => state.library.symbol)
  const colors = data
    ? data.colors
    : useSelector((state: AppState) => state.manager.project.colors)

  const getHydratedBlockById = (id: ID): HydratedBlock | null => {
    const block = blocks[id] as Block

    if (!block) {
      return null
    }

    const falseBlock: HydratedBlock = {
      id: '',
      image: null,
      type: 'monolithic',
      completed: false,
      title: '',
      colorId: '',
      wireframes: [],
      content: '',
    }

    if (block.type === 'symbol') {
      const symbol = symbols.find((s) => s.id === block.symbolId) as SymbolItem
      const symbolData = resolveSymbol(symbol, library, colors)

      if (!symbol || !symbolData) {
        return {
          ...falseBlock,
          id: block.id,
          completed: block.completed,
          type: 'symbol',
          symbolId: block.symbolId,
        }
      }

      const { visual } = symbolData
      const wireframes = visual.type === 'wireframes' ? visual.wireframes : []
      const image = visual.type === 'image' ? visual.image : null

      return {
        id: block.id,
        type: 'symbol',
        completed: block.completed,
        collapsed: block.collapsed,

        title: symbolData.title,
        colorId: symbolData.colorId,
        wireframes,
        content: symbolData.content,
        image,
        symbolId: block.symbolId,
      }
    }

    if (block.type === 'monolithic') {
      // @TODO review this and fix
      // @ts-expect-error - Type 'MonolithicBlock' is missing properties from type 'HydratedBlock'
      return block
    }

    return falseBlock
  }

  const getHydratedBlocks = (): HydratedBlock[] => {
    return Object.keys(blocks).reduce<HydratedBlock[]>((acc, blockId) => {
      const hydratedBlock = getHydratedBlockById(blockId)

      if (hydratedBlock) {
        return [...acc, hydratedBlock]
      }

      return acc
    }, [])
  }

  return {
    getHydratedBlockById,
    getHydratedBlocks,
  }
}
